/**
 * A fixed-term series billed by the class (`programs.prorate_late_joins`, BHCS-152).
 *
 * The parent picks the first class their child will attend, and the invoice charges the classes
 * from that one to the end of the term. The clock never picks it: "today" only stops a parent
 * from choosing a class that has already happened.
 *
 * This is the page's half of the rule — which classes can still be picked, which dates the
 * family pays for, and what each price line becomes. The invoice's half is
 * `create_invoice_on_enrollment()` over `program_session_dates()` in SQL. Both count classes with
 * the rule in `isSessionOn`, both scale every non-materials line by classes billed / classes in
 * the term, and both round each line to the cent half away from zero, so the price a parent
 * reads here is the one they are billed.
 */

import { isSessionOn, closureReasonOn, type SessionDateProgram } from './programSessionDates';
import { getSessionWeekdays, shiftDateString, weekdayOfDateString } from './schedule';
import { parseCron } from '../types/program';
import { BUSINESS_TIMEZONE } from './date';
import { DEFAULT_BRAND_KEY, isBrandKey, type BrandKey } from '../config/brands';

/** The slice of a program this reads. */
export interface ClassBilledProgram extends SessionDateProgram {
  billing_type: string;
  prorate_late_joins?: boolean | null;
  brand_key?: string | null;
  /** Class length in minutes; with the cron start time, when today's class is over. */
  duration?: number | null;
}

/** True when this program bills a late joiner for the classes they will attend. */
export function isBilledByClass(program: Pick<ClassBilledProgram, 'billing_type' | 'prorate_late_joins'>): boolean {
  return program.billing_type === 'fixed_term' && !!program.prorate_late_joins;
}

function brandOf(program: ClassBilledProgram): BrandKey {
  return isBrandKey(program.brand_key) ? program.brand_key : DEFAULT_BRAND_KEY;
}

/** Every class in the term, oldest first. The SQL twin is `program_session_dates()`. */
export function termClasses(program: ClassBilledProgram): string[] {
  const brand = brandOf(program);
  const classes: string[] = [];
  for (let d = program.start_date; d <= program.end_date; d = shiftDateString(d, 1)) {
    if (isSessionOn(program, d, brand)) classes.push(d);
  }
  return classes;
}

/** "Now" in the business timezone: the date and the minutes since midnight. */
export interface BusinessNow {
  today: string;
  minutesNow: number;
}

export function businessNow(at: Date = new Date()): BusinessNow {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: BUSINESS_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(at);
  const part = (type: Intl.DateTimeFormatPartTypes) => parts.find((p) => p.type === type)?.value ?? '';
  return {
    today: `${part('year')}-${part('month')}-${part('day')}`,
    minutesNow: Number(part('hour')) * 60 + Number(part('minute')),
  };
}

/** Minutes since midnight when a class ends, or null when the cron carries no usable time. */
function classEndMinutes(program: ClassBilledProgram): number | null {
  const { hour, minute } = parseCron(program.schedule_cron);
  if (!Number.isInteger(hour) || !Number.isInteger(minute)) return null;
  return hour * 60 + minute + (program.duration ?? 60);
}

/**
 * The classes a parent may choose to start with: every class from today on, except today's once
 * it is over. Whether to include today's class is the parent's call while it can still be
 * attended, so it is offered right up to the end of the class.
 */
export function firstClassOptions(program: ClassBilledProgram, now: BusinessNow): string[] {
  const end = classEndMinutes(program);
  return termClasses(program).filter((d) => {
    if (d > now.today) return true;
    if (d < now.today) return false;
    return end === null || now.minutesNow < end;
  });
}

/**
 * The class to preselect, or '' to make the parent choose. Only when every remaining class is
 * still ahead of today — the series has not started, or today's class is not an option — is
 * the first one the obvious answer. When today's class is on offer, preselecting it would
 * decide for the parent whether today is included, so nothing is preselected.
 */
export function defaultFirstClass(options: readonly string[], allClasses: readonly string[], today: string): string {
  if (options.length === 0) return '';
  const seriesNotStarted = options[0] === allClasses[0];
  return seriesNotStarted && options[0] > today ? options[0] : '';
}

export type TermDayStatus = 'billed' | 'before' | 'no-class';

export interface TermDay {
  date: string;
  /**
   * `billed` — a class from the chosen first class on; the family pays for it.
   * `before` — a class before the chosen first class; not billed.
   * `no-class` — a scheduled weekday the program does not meet (its own day off or a closure);
   *   never billed, listed so the count can be checked.
   */
  status: TermDayStatus;
  /** For `no-class`: the closure's label, when a closure is the reason. */
  reason?: string;
}

/** Every scheduled day in the term, each marked billed, before the start, or no class. */
export function termDays(program: ClassBilledProgram, firstClass: string): TermDay[] {
  const brand = brandOf(program);
  const weekdays = getSessionWeekdays(program.schedule_cron);
  const listsDates = (program.selective_dates?.length ?? 0) > 0;
  const days: TermDay[] = [];
  for (let d = program.start_date; d <= program.end_date; d = shiftDateString(d, 1)) {
    if (isSessionOn(program, d, brand)) {
      days.push({ date: d, status: d >= firstClass ? 'billed' : 'before' });
    } else if (!listsDates && weekdays.has(weekdayOfDateString(d))) {
      days.push({ date: d, status: 'no-class', reason: closureReasonOn(program, d, brand) });
    }
  }
  return days;
}

/** How many of the term's classes a given first class bills. */
export interface ClassShare {
  billed: number;
  total: number;
}

export function classShare(program: ClassBilledProgram, firstClass: string): ClassShare {
  const classes = termClasses(program);
  return { billed: classes.filter((d) => d >= firstClass).length, total: classes.length };
}

/**
 * One price line for the classes billed: `amount × billed / total`, rounded to the cent half
 * away from zero — what Postgres `ROUND(amount * billed / total, 2)` does, discounts included.
 * Worked in whole cents so a float like 0.1 never tips a half-cent the wrong way.
 */
export function prorateAmount(amount: number, share: ClassShare): number {
  if (share.total <= 0 || share.billed >= share.total) return amount;
  const cents = Math.round(Math.abs(amount) * 100);
  const scaled = Math.round((cents * share.billed) / share.total);
  return (Math.sign(amount) * scaled) / 100;
}

/**
 * The arithmetic behind a prorated line, for display: "9 × $40.00" when the line divides into
 * whole cents per class, otherwise "$400.00 × 9/11".
 */
export function describeProration(amount: number, share: ClassShare): string {
  const cents = Math.round(Math.abs(amount) * 100);
  const sign = amount < 0 ? '-' : '';
  if (cents % share.total === 0) {
    return `${share.billed} × ${sign}$${(cents / share.total / 100).toFixed(2)}`;
  }
  return `${sign}$${(cents / 100).toFixed(2)} × ${share.billed}/${share.total}`;
}
